import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, MapPin, Clock, User, Car, Navigation, AlertTriangle,
  CheckCircle, XCircle, Copy, ExternalLink, RefreshCw, Plus,
  Calendar, CreditCard, Banknote, MessageSquare, Search, Filter,
  ChevronRight, MoreVertical, PhoneCall, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import useWebSocket from '../../hooks/useWebSocket';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { formatCurrency, formatRelativeTime, formatPhone } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRideSource = (ride = {}) => {
  const src = (ride.source || '').toLowerCase();
  const passengerType = (ride.passenger_type || '').toLowerCase();

  if (src.includes('website') || passengerType === 'website') {
    return { key: 'website', label: 'Website', className: 'bg-purple-100 text-purple-700' };
  }
  if (src.includes('passenger_app') || passengerType === 'app') {
    return { key: 'app', label: 'App', className: 'bg-blue-100 text-blue-700' };
  }
  if (src.includes('dispatcher')) {
    return { key: 'dispatcher', label: 'Dispatcher', className: 'bg-gray-100 text-gray-700' };
  }
  return { key: 'other', label: 'Tjetër', className: 'bg-gray-100 text-gray-700' };
};

const statusConfig = {
  pending: { label: 'Në Pritje', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  accepted: { label: 'Pranuar', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  arriving: { label: 'Duke Arritur', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  in_progress: { label: 'Në Udhëtim', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  completed: { label: 'Përfunduar', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500' },
  cancelled: { label: 'Anuluar', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  no_show: { label: 'No-Show', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  scheduled: { label: 'Planifikuar', bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' }
};

const LiveDispatchScreen = ({ onCreateBooking }) => {
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, pending, scheduled, problem
  const [searchTerm, setSearchTerm] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [stats, setStats] = useState(null);
  const [showIncomingRidePopup, setShowIncomingRidePopup] = useState(false);
  const [incomingRidePopupData, setIncomingRidePopupData] = useState(null);

  // WebSocket for instant new-ride popup and live driver positions
  useWebSocket('dispatcher', (message) => {
    const locationTypes = [
      'driver.location.updated',
      'driver_location_updated',
      'driver_moved',
      'driver_location',
    ];
    if (locationTypes.includes(message.type)) {
      const driverId = message.driver_id;
      const position = message.position || { lat: message.lat, lng: message.lng };
      if (driverId && position?.lat != null && position?.lng != null) {
        const nextPosition = { lat: position.lat, lng: position.lng };
        setDrivers((prev) =>
          prev.map((d) => (d.id === driverId ? { ...d, position: nextPosition } : d))
        );
        setSelectedDriver((prev) =>
          prev?.id === driverId ? { ...prev, position: nextPosition } : prev
        );
      }
      return;
    }

    const incomingRide = message?.data || message?.ride;
    if (!incomingRide) return;

    if (message.type === 'new_ride' || message.type === 'new_airport_ride') {
      const source = getRideSource(incomingRide);
      toast.info(`Rezervim i ri nga ${source.label}`, {
        description: incomingRide.passenger_name || incomingRide.passenger_phone || 'Porosi e re',
        duration: 5000,
      });

      // Pull latest list and focus latest incoming request in details panel.
      setSelectedRide(incomingRide);
      setIncomingRidePopupData(incomingRide);
      setShowIncomingRidePopup(true);
      fetchData();
    }

    if (message.type === 'ride_updated' || message.type === 'ride_assigned') {
      fetchData();
    }
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch rides based on filter
      let ridesUrl = `${API_URL}/api/rides`;
      if (filter === 'active') {
        ridesUrl += '?status=pending,accepted,arriving,in_progress';
      } else if (filter === 'pending') {
        ridesUrl += '?status=pending';
      } else if (filter === 'scheduled') {
        ridesUrl += '?scheduled=true';
      } else if (filter === 'problem') {
        ridesUrl += '?status=cancelled,no_show';
      }

      const [ridesRes, driversRes, statsRes] = await Promise.all([
        fetch(ridesUrl, { headers }),
        fetch(`${API_URL}/api/drivers`, { headers }),
        fetch(`${API_URL}/api/stats/daily`, { headers })
      ]);

      if (ridesRes.ok) {
        const ridesData = await ridesRes.json();
        setRides(ridesData);
      }

      if (driversRes.ok) {
        const driversData = await driversRes.json();
        setDrivers(driversData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAssignDriver = async (rideId, driverId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/rides/${rideId}/assign?driver_id=${encodeURIComponent(driverId)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Shoferi u caktua me sukses!');
        setShowAssignModal(false);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Gabim në caktimin e shoferit');
      }
    } catch (error) {
      toast.error('Gabim në lidhje me serverin');
    }
  };

  const handleCancelRide = async () => {
    if (!cancelReason.trim()) {
      toast.error('Arsyeja është e detyrueshme');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/rides/${selectedRide.id}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancelReason })
      });

      if (response.ok) {
        toast.success('Udhëtimi u anulua');
        setShowCancelModal(false);
        setCancelReason('');
        setSelectedRide(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Gabim');
      }
    } catch (error) {
      toast.error('Gabim në lidhje me serverin');
    }
  };

  const handleMarkPOS = async (rideId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/rides/${rideId}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          payment_method: 'pos',
          payment_status: 'paid'
        })
      });

      if (response.ok) {
        toast.success('Pagesa POS u regjistrua');
        fetchData();
      }
    } catch (error) {
      toast.error('Gabim');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('U kopjua!');
  };

  // Using formatRelativeTime from utils instead of local getTimeSince

  const availableDrivers = drivers.filter(d => d.status === 'online' || d.status === 'available');
  const busyDrivers = drivers.filter(d => d.status === 'busy');
  const offlineDrivers = drivers.filter(d => d.status === 'offline');

  const filteredRides = rides.filter(r => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      r.passenger_name?.toLowerCase().includes(search) ||
      r.passenger_phone?.includes(search) ||
      r.pickup_address?.toLowerCase().includes(search) ||
      r.ride_number?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="h-[calc(100vh-140px)] flex gap-4">
      {/* LEFT: Queue */}
      <div className="w-[380px] flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Queue Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">Porositë</h2>
            <div className="flex items-center gap-2">
              <button 
                onClick={fetchData}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={onCreateBooking}
                className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                <Plus className="w-4 h-4" />
                Porosi
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { id: 'active', label: 'Aktive', count: rides.filter(r => ['pending', 'accepted', 'arriving', 'in_progress'].includes(r.status)).length },
              { id: 'pending', label: 'Pritje', count: rides.filter(r => r.status === 'pending').length },
              { id: 'scheduled', label: 'Plan.', count: rides.filter(r => r.scheduled).length },
              { id: 'problem', label: 'Problem', count: rides.filter(r => ['cancelled', 'no_show'].includes(r.status)).length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition ${
                  filter === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                    filter === tab.id ? 'bg-red-100 text-red-700' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Kërko..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Queue List */}
        <div className="flex-1 overflow-y-auto">
          {loading && rides.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 text-gray-300 animate-spin" />
              <p className="text-sm text-gray-500">Duke ngarkuar...</p>
            </div>
          ) : filteredRides.length === 0 ? (
            <div className="p-8 text-center">
              <Car className="w-10 h-10 mx-auto mb-2 text-gray-200" />
              <p className="text-sm text-gray-500">Nuk ka porosi</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredRides.map(ride => {
                const status = statusConfig[ride.status] || statusConfig.pending;
                const isSelected = selectedRide?.id === ride.id;
                
                return (
                  <div 
                    key={ride.id}
                    onClick={() => setSelectedRide(ride)}
                    className={`p-3 cursor-pointer transition hover:bg-gray-50 ${
                      isSelected ? 'bg-red-50 border-l-4 border-red-500' : ''
                    }`}
                  >
                    {/* Header Row */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded">
                          {ride.ride_number || `#${ride.id?.slice(-6).toUpperCase()}`}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.text}`}>
                          {status.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(ride.created_at)}
                      </span>
                    </div>

                    {/* Passenger */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{ride.passenger_name || 'Pasagjer'}</p>
                        <p className="text-xs text-gray-500">{ride.passenger_phone || '-'}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        ride.payment_method === 'pos' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {ride.payment_method === 'pos' ? 'POS' : 'Cash'}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRideSource(ride).className}`}>
                        {getRideSource(ride).label}
                      </span>
                    </div>

                    {/* Route */}
                    <div className="space-y-1">
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center mt-0.5">
                          <div className="w-1.5 h-1.5 bg-white rounded-full" />
                        </div>
                        <p className="text-xs text-gray-600 flex-1 line-clamp-1">{ride.pickup_address}</p>
                      </div>
                      {ride.dropoff_address && (
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center mt-0.5">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          </div>
                          <p className="text-xs text-gray-600 flex-1 line-clamp-1">{ride.dropoff_address}</p>
                        </div>
                      )}
                    </div>

                    {/* Driver */}
                    {ride.driver_name && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                        <Car className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-600">{ride.driver_name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* CENTER: Drivers List */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Drivers Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg">Shoferët</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                {availableDrivers.length} Online
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" />
                {busyDrivers.length} Busy
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                {offlineDrivers.length} Offline
              </span>
            </div>
          </div>
        </div>

        {/* Drivers List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            {drivers.map(driver => {
              const isOnline = driver.status === 'online' || driver.status === 'available';
              const isBusy = driver.status === 'busy';
              const isSelected = selectedDriver?.id === driver.id;
              
              return (
                <div 
                  key={driver.id}
                  onClick={() => setSelectedDriver(driver)}
                  className={`p-3 rounded-xl border cursor-pointer transition ${
                    isSelected 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        isOnline ? 'bg-green-100 text-green-700' : isBusy ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {driver.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                        isOnline ? 'bg-green-500' : isBusy ? 'bg-yellow-500' : 'bg-gray-400'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{driver.name}</p>
                      <p className="text-xs text-gray-500">
                        {driver.vehicle_plate || 'Pa targë'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isOnline ? 'bg-green-100 text-green-700' : isBusy ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {isOnline ? 'Disponueshëm' : isBusy ? 'Në udhëtim' : 'Offline'}
                    </span>
                    
                    {selectedRide && isOnline && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAssignDriver(selectedRide.id, driver.id);
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700"
                      >
                        Cakto
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* RIGHT: Details Panel */}
      <div className="w-[350px] flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden">
        {selectedRide ? (
          <>
            {/* Ride Details Header */}
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 bg-red-600 text-white font-bold rounded">
                  {selectedRide.ride_number || `#${selectedRide.id?.slice(-6).toUpperCase()}`}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger className="p-2 hover:bg-gray-200 rounded-lg">
                    <MoreVertical className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowCancelModal(true)} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-2" />
                      Anulo
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Edit className="w-4 h-4 mr-2" />
                      Modifiko
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Details Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Passenger Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{selectedRide.passenger_name || 'Pasagjer'}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{selectedRide.passenger_phone}</span>
                      {selectedRide.passenger_phone && (
                        <>
                          <button 
                            onClick={() => copyToClipboard(selectedRide.passenger_phone)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Copy className="w-3 h-3 text-gray-400" />
                          </button>
                          <a 
                            href={`tel:${selectedRide.passenger_phone}`}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <PhoneCall className="w-3 h-3 text-green-600" />
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Route */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700">Rruga</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-green-600 font-medium">Nisja</p>
                      <p className="text-sm">{selectedRide.pickup_address}</p>
                    </div>
                  </div>
                  {selectedRide.dropoff_address && (
                    <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                      <Navigation className="w-4 h-4 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-red-600 font-medium">Mbërritja</p>
                        <p className="text-sm">{selectedRide.dropoff_address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedRide.notes && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-yellow-600" />
                    <span className="text-xs font-medium text-yellow-700">Shënime</span>
                  </div>
                  <p className="text-sm">{selectedRide.notes}</p>
                </div>
              )}

              {/* Driver */}
              {selectedRide.driver_name && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Car className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">{selectedRide.driver_name}</p>
                        <p className="text-xs text-gray-500">{selectedRide.driver_phone}</p>
                      </div>
                    </div>
                    {selectedRide.driver_phone && (
                      <a 
                        href={`tel:${selectedRide.driver_phone}`}
                        className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200"
                      >
                        <PhoneCall className="w-4 h-4 text-blue-600" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Payment */}
              <div className="border border-gray-100 rounded-lg p-4">
                <h4 className="font-medium text-sm text-gray-700 mb-3">Pagesa</h4>
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    selectedRide.payment_method === 'pos' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedRide.payment_method === 'pos' ? (
                      <><CreditCard className="w-4 h-4 inline mr-1" /> POS</>
                    ) : (
                      <><Banknote className="w-4 h-4 inline mr-1" /> Cash</>
                    )}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedRide.payment_status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {selectedRide.payment_status === 'paid' ? 'Paguar' : 'Pa paguar'}
                  </span>
                </div>
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedRide.final_price || selectedRide.estimated_price || 0)}</span>
                  </div>
                
                {selectedRide.status === 'completed' && selectedRide.payment_status !== 'paid' && (
                  <button
                    onClick={() => handleMarkPOS(selectedRide.id)}
                    className="w-full mt-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Shëno si Paguar (POS)
                  </button>
                )}
              </div>

              {/* Source */}
              <div className="border border-gray-100 rounded-lg p-4">
                <h4 className="font-medium text-sm text-gray-700 mb-2">Burimi</h4>
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getRideSource(selectedRide).className}`}>
                  {getRideSource(selectedRide).label}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-gray-100 space-y-2">
              {!selectedRide.driver_id && selectedRide.status === 'pending' && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="w-full py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
                >
                  Cakto Shofer
                </button>
              )}
              {selectedRide.driver_id && ['accepted', 'arriving', 'in_progress'].includes(selectedRide.status) && (
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Ndrysho Shofer
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Car className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-500">Zgjidh një porosi</p>
              <p className="text-sm text-gray-400">për të parë detajet</p>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Anulo Udhëtimin</h3>
            <p className="text-sm text-gray-600 mb-4">
              Arsyeja e anulimit është e detyrueshme për audit.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Shkruaj arsyen..."
              className="w-full p-3 border border-gray-200 rounded-lg mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Anulo
              </button>
              <button
                onClick={handleCancelRide}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Konfirmo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incoming Ride Popup */}
      {showIncomingRidePopup && incomingRidePopupData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold mb-2">Rezervim i Ri</h3>
            <p className="text-sm text-gray-600 mb-4">
              Burimi: <span className={`px-2 py-1 rounded text-xs font-medium ${getRideSource(incomingRidePopupData).className}`}>{getRideSource(incomingRidePopupData).label}</span>
            </p>

            <div className="space-y-2 text-sm mb-5">
              <p><span className="font-medium">Klienti:</span> {incomingRidePopupData.passenger_name || 'Pa emër'}</p>
              <p><span className="font-medium">Telefon:</span> {incomingRidePopupData.passenger_phone || '-'}</p>
              <p><span className="font-medium">Nisja:</span> {incomingRidePopupData.pickup?.address || incomingRidePopupData.pickup_address || 'N/A'}</p>
              <p><span className="font-medium">Destinacioni:</span> {incomingRidePopupData.dropoff?.address || incomingRidePopupData.destination?.address || incomingRidePopupData.dropoff_address || 'N/A'}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowIncomingRidePopup(false);
                  setIncomingRidePopupData(null);
                }}
                className="flex-1 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Mbyll
              </button>
              <button
                onClick={() => {
                  setSelectedRide(incomingRidePopupData);
                  setShowIncomingRidePopup(false);
                  setIncomingRidePopupData(null);
                }}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Shiko Detajet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDispatchScreen;
